import { useState } from "react";
import { Minus, Plus, Info } from "lucide-react";
import { type Product } from "@/data/pricing";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import ProductInfoPanel from "@/components/ProductInfoPanel";

interface ProductSelectorProps {
  product: Product;
  quantity: number;
  onAdd: () => void;
  onRemove: () => void;
  disabled: boolean;
}

const ProductSelector = ({ product, quantity, onAdd, onRemove, disabled }: ProductSelectorProps) => {
  const [infoOpen, setInfoOpen] = useState(false);

  return (
    <div
      className={`group relative rounded-2xl overflow-hidden bg-gradient-to-br ${product.gradient} aspect-square p-5 flex flex-col justify-end gap-3 transition-all duration-300 hover:scale-[1.02] ${
        quantity > 0 ? "ring-2 ring-foreground/20" : ""
      }`}
    >
      {/* Info trigger (top-right) */}
      <Sheet open={infoOpen} onOpenChange={setInfoOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-foreground/10 hover:bg-foreground/20 backdrop-blur-sm flex items-center justify-center transition-all active:scale-90 z-10"
            aria-label={`Visa info om ${product.name}`}
          >
            <Info className="w-3.5 h-3.5 text-foreground" />
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="text-left mb-2">
            <div className={`-mx-6 -mt-6 px-6 pt-8 pb-6 bg-gradient-to-br ${product.gradient} mb-4`}>
              <img
                src={product.image}
                alt={product.name}
                className="w-24 h-24 object-contain drop-shadow mb-3"
              />
              <SheetTitle className="text-2xl font-black uppercase tracking-tight text-foreground">
                {product.name}
              </SheetTitle>
              <p className="text-xs text-foreground/70 mt-1 tracking-wide">
                {product.description}
              </p>
            </div>
          </SheetHeader>
          <ProductInfoPanel productId={product.id} />
        </SheetContent>
      </Sheet>

      {/* Title + description (anchored near bottom) */}
      <div>
        <h3 className="text-base md:text-lg font-black uppercase text-foreground tracking-tight leading-none">
          {product.name}
        </h3>
        <p className="text-[10px] text-foreground/60 mt-1.5 tracking-wide leading-snug">
          {product.description}
        </p>
      </div>

      {/* Bottom: Quantity controls */}
      <div className="flex items-center justify-between">
        <span className="text-3xl font-black text-foreground/90 tabular-nums leading-none">
          {quantity}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onRemove}
            disabled={quantity === 0}
            className="w-8 h-8 rounded-full bg-foreground/10 hover:bg-foreground/20 disabled:opacity-20 flex items-center justify-center transition-all active:scale-90"
            aria-label={`Ta bort ${product.name}`}
          >
            <Minus className="w-3.5 h-3.5 text-foreground" />
          </button>
          <button
            onClick={onAdd}
            disabled={disabled}
            className="w-8 h-8 rounded-full bg-foreground hover:bg-foreground/90 disabled:opacity-20 flex items-center justify-center transition-all active:scale-90"
            aria-label={`Lägg till ${product.name}`}
          >
            <Plus className="w-3.5 h-3.5 text-background" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelector;
